import { computed } from "vue";

import type { CardProps } from "./useCardData";

export function useRevUpCampaign(
  props: CardProps,
  revupCampaigns: any,
  hasAccessToCampaign: () => boolean
) {
  const checkRevupInGlobalProducts = (): boolean => {
    return (
      props.result?.global_products?.some((product: any) =>
        revupCampaigns.value.some(
          (bundle: any) => product?.content_bundle_id === bundle.id
        )
      ) || false
    );
  };

  const checkRevupInContentBundle = (contentBundle: any): boolean => {
    return revupCampaigns.value.some((bundle: any) =>
      contentBundle.some((id: any) => id === bundle.id)
    );
  };

  const isRevup = computed((): boolean => {
    if (!window.env.ENABLE_REV_UP_RECERT || props.result?.type !== "path") {
      return false;
    }

    const contentBundle = props.result?.content_bundle;
    if (!contentBundle || contentBundle?.length === 0) {
      return checkRevupInGlobalProducts();
    }

    return checkRevupInContentBundle(contentBundle);
  });

  const showRevUpRecertCampaign = computed((): boolean => {
    const campaignGuids = Array.isArray(window.env.REV_UP_CAMPAIGN_GUIDs)
      ? (window.env.REV_UP_CAMPAIGN_GUIDs as string[])
      : [];
    const start = new Date(window.env.REV_UP_CAMPAIGN_START);
    const end = new Date(window.env.REV_UP_CAMPAIGN_END);

    const today = new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });

    const isDCFNDU =
      !!props.result?.guid &&
      Array.isArray(window.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU) &&
      window.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU.includes(props.result.guid) &&
      new Date(today) >= new Date(window.env.REV_UP_CAMPAIGN_START_DCFNDU) &&
      new Date(today) <= new Date(window.env.REV_UP_CAMPAIGN_END_DCFNDU);

    return (
      (campaignGuids.includes(props.result?.guid ?? "") &&
        new Date(today) >= start &&
        new Date(today) <= end) ||
      isDCFNDU
    );
  });

  const getCampaignEndDate = (): Date | null => {
    for (const bundle of revupCampaigns.value) {
      if (props.result?.content_bundle?.includes(bundle?.id)) {
        return new Date(bundle?.global_product?.eol_date);
      }
    }
    return null;
  };

  const getGlobalProductEndDate = (): Date | null => {
    for (const product of props.result?.global_products || []) {
      for (const bundle of revupCampaigns.value) {
        if (product?.content_bundle_id === bundle.id) {
          return new Date(bundle?.global_product?.eol_date);
        }
      }
    }
    return null;
  };

  const freeUntil = computed((): string => {
    if (!window.env.ENABLE_REV_UP_RECERT || props.result?.type !== "path") {
      return "";
    }

    let campaignEndMessage = "Free until ";
    const contentBundle = props.result?.content_bundle;

    let endDate = null;
    if (!contentBundle || contentBundle.length === 0) {
      endDate = getGlobalProductEndDate();
    } else {
      endDate = getCampaignEndDate();
    }

    if (endDate) {
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      };
      campaignEndMessage += endDate.toLocaleDateString("en-US", options);
    }

    if (isRevup.value && hasAccessToCampaign()) {
      return campaignEndMessage;
    }

    return "";
  });

  return {
    isRevup,
    showRevUpRecertCampaign,
    freeUntil,
    checkRevupInGlobalProducts,
    checkRevupInContentBundle,
    getCampaignEndDate,
    getGlobalProductEndDate,
  };
}
